<script setup lang="ts">
import { ref, watch } from 'vue'
import GwBoundingBox from '@/components/lib/GwBoundingBox.vue'
import { vueVimeoPlayer as VueVimeoPlayer } from 'vue-vimeo-player'
import {
  YoutubeIframe as VueYoutubePlayer,
  PlayerState as YoutubeState,
} from '@vue-youtube/component'

interface GwVideoPlayerProps {
  src: string
  autoplay?: boolean
  isVisible?: boolean
  type: 'youtube' | 'vimeo'
  start?: number
}

const props = withDefaults(defineProps<GwVideoPlayerProps>(), {
  isVisible: true,
})

const emit = defineEmits(['vimeo:play', 'vimeo:ended'])

const isReady = ref(true)
// @vue-youtube/component has typing but it doesn't include the instance of Youtube player
const youtube = ref<any>(null)

function onReady() {
  isReady.value = true
}

watch(
  () => props.isVisible,
  (visible: boolean) => !visible && youtube.value?.instance?.stopVideo(),
)

watch(
  () => props.src,
  () => {
    isReady.value = false
  },
)

watch(
  () => props.start,
  () => {
    // Only seek if the video is already playing (otherwise it starts on it's own)
    const youtubeState = youtube.value?.instance?.getPlayerState()
    if (props.type === 'youtube' && props.start) {
      if (youtubeState !== YoutubeState.VIDEO_CUED) {
        // Video is playing or paused, seek to start time
        youtube.value?.instance?.seekTo(props.start, true)
      } else {
        // Video is in the cue state (showing big play button), this forces the start time
        youtube.value?.instance?.cueVideoById(props.src, props.start)
      }
    }
  },
)
</script>

<template>
  <GwBoundingBox>
    <template #default="{ height, width }">
      <div
        ref="video_container"
        class="d-flex f-center flex-1"
        :style="`min-height: ${height}px; min-width: ${width}px;`"
      >
        <div
          v-if="!isReady"
          class="mdi clr-grey"
          style="font-size: 6.25rem; --pulse-duration: 4s; --pulse-scale: 1.125; position: absolute"
          :class="{
            opacity_0: isReady,
            'anim-pulse': !isReady,
            'mdi-vimeo': type === 'vimeo',
            'mdi-youtube': type === 'youtube',
          }"
        ></div>
        <div
          v-if="type === 'youtube'"
          :key="src"
          :class="{ opacity_0: !isReady }"
          class="youtube-container"
          :style="{
            width: `${width}px`,
            height: `${height}px`,
          }"
        >
          <VueYoutubePlayer
            ref="youtube"
            :video-id="src"
            :width="width"
            :height="height"
            :player-vars="{
              autoplay: autoplay ? 1 : 0,
              start: start ? start : undefined,
            }"
            @ready="onReady"
          />
        </div>
        <VueVimeoPlayer
          v-if="type === 'vimeo'"
          ref="vimeo"
          :key="src"
          :src="src"
          :autoplay="autoplay"
          :player-width="width"
          :player-height="height"
          :video-id="src"
          :options="{
            colors: ['000000', '00ADEF', 'FFFFFF', '000000'],
          }"
          :class="{ opacity_0: !isReady }"
          @play="emit('vimeo:play')"
          @ended="emit('vimeo:ended')"
          @ready="onReady"
        />
      </div>
    </template>
  </GwBoundingBox>
</template>

<style scoped>
.youtube-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.youtube-container :deep(iframe) {
  position: absolute;
  width: 100% !important;
  height: 100% !important;
}
</style>
